import * as React from 'react';

import {colors} from 'shells/colors/colors';
import {Stack} from 'shells/layout/stack/stack';
import {Switch} from 'shells/switch/switch';
import {Body} from 'shells/typography/body';

import type {GetSessionUser_loggedInUser as User} from 'nutshell-graphql-types';

import {useUpdateUserSetting} from '../../../core/use-update-user-setting';

import {MenuItem} from './menu-item';

type Props = {
    user: User;
    icon: React.ComponentType<any>;
    label: string;
    propertyOnUser: string;
    settingName: string;
    reloadOnChange?: boolean;
    isSecondary?: boolean;
    activeBackgroundColor?: string;
    subText?: React.ReactNode;
    // This is to support that some menu-items need to know the state of other items
    getState?: (isEnabled: boolean) => void;
};

export const MenuItemSettingsToggle = (props: Props) => {
    const {reloadOnChange = false, isSecondary = false, activeBackgroundColor, getState} = props;

    // @ts-expect-error
    const [isEnabled, setIsEnabled] = React.useState<boolean>(props.user[props.propertyOnUser]);

    const updateUserSetting = useUpdateUserSetting();

    React.useEffect(() => {
        if (getState) {
            getState(isEnabled);
        }
    }, [isEnabled, getState]);

    const backgroundColor = React.useMemo(() => {
        const disabledBackgroundColor = isSecondary ? colors.offWhite : colors.white;
        const enableBackgroundColor = activeBackgroundColor
            ? activeBackgroundColor
            : disabledBackgroundColor;

        return isEnabled ? enableBackgroundColor : disabledBackgroundColor;
    }, [isEnabled, activeBackgroundColor, isSecondary]);

    return (
        <MenuItem
            icon={props.icon}
            text={
                <Stack spacing={4}>
                    <Stack justifyContent='space-between' isHorizontal={true}>
                        <Body
                            color={props.isSecondary ? 'light' : 'medium'}
                            isItalic={props.isSecondary}
                            size={props.isSecondary ? 'small' : 'normal'}
                        >
                            {props.label}
                        </Body>
                        <Switch
                            isOn={isEnabled}
                            onChange={() => {
                                setIsEnabled(!isEnabled);
                                updateUserSetting({
                                    userId: props.user.id,
                                    settingName: props.settingName,
                                    value: !isEnabled ? '1' : '0',
                                    propertyOnUser: props.propertyOnUser,
                                }).then(() => {
                                    if (reloadOnChange) {
                                        window.location.reload();
                                    }
                                });
                            }}
                            isRounded={true}
                            isSmall={true}
                        />
                    </Stack>
                    {props.subText && (
                        <Body size='small' color='light'>
                            {props.subText}
                        </Body>
                    )}
                </Stack>
            }
            iconColor={colors.grey}
            bodyFill={true}
            backgroundColor={backgroundColor}
        />
    );
};
